import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../styles/DropdownSystem.css";

export default function StyledSelect({
  value,
  options,
  onChange,
  className = "",
  minWidth = 160,
  ariaLabel = "Select option",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      const target = event.target;
      if (
        !wrapperRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsideClick);
    return () => document.removeEventListener("pointerdown", handleOutsideClick);
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const updateMenuPosition = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;

      setMenuPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [open]);

  const selected = options.find((option) => String(option.value) === String(value));
  const menu =
    open && menuPosition
      ? createPortal(
          <div
            ref={menuRef}
            className="nw-styled-select-menu"
            role="listbox"
            style={menuPosition}
          >
            {options.map((option) => {
              const active = String(option.value) === String(value);
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`nw-styled-select-option ${active ? "active" : ""}`}
                  role="option"
                  aria-selected={active}
                  onClick={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>,
          document.body
        )
      : null;

  return (
    <div
      ref={wrapperRef}
      className={`nw-styled-select ${className}`.trim()}
      style={{ minWidth }}
    >
      <button
        type="button"
        className={`nw-styled-select-toggle ${open ? "open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
      >
        <span>{selected?.label || "Select"}</span>
        <i className="fa-solid fa-chevron-down" aria-hidden="true" />
      </button>

      {menu}
    </div>
  );
}
